import { getAccessToken } from "@/utils/tokenStore";
/**
 * 브라우저 푸시 알림 시스템 관리자
 * 사용자 로그인 후 자동으로 알림 시스템을 초기화하고 관리합니다.
 */
import {jwtDecoder} from '@/utils/utils';

class BrowserNotificationManager {
    constructor(apiInstance, router) {
        this.apiInstance = apiInstance;
        this.router = router;
        
        // 상태 관리
        this.isInitialized = false;
        this.checkTimer = null;
        this.checkInterval = 5 * 60 * 1000; // 5분마다 확인
        this.retryCount = 0;
        this.maxRetries = 3;
        
        // Service Worker 유효성 연장하기 위한 핑 관리
        this.pingTimer = null;
        this.pingInterval = 30 * 1000; // 30초마다 핑
        
        // 이벤트 리스너 바인딩
        this.handleStorageChange = this.handleStorageChange.bind(this);
        this.handleRouteChange = this.handleRouteChange.bind(this);
    }

    /**
     * 사용자가 수동으로 구독 해제했는지 확인
     * @returns {boolean} 사용자가 수동으로 해제한 경우 true
     */
    isUserUnsubscribed() {
        const userUnsubscribed = localStorage.getItem('browserNotificationUserUnsubscribed');
        return userUnsubscribed === 'true';
    }

    /**
     * 알림 시스템 초기화 및 감지 시작
     */
    init() {
        console.log('[BrowserNotificationManager] 초기화 시작');
        
        // Service Worker 메시지 리스너 추가
        this.setupServiceWorkerListener();
        
        // 로그인 상태 감지 시작
        this.watchLoginStatus();
    }

    /**
     * Service Worker 메시지 리스너 설정
     */
    setupServiceWorkerListener() {
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.addEventListener('message', (event) => {
                console.log('[BrowserNotificationManager] Service Worker 메시지 수신:', event.data);
                
                if (event.data && event.data.type === 'NAVIGATE_TO') {
                    const targetUrl = event.data.url;
                    const targetPath = targetUrl.replace(window.location.origin, '');
                    console.log('[BrowserNotificationManager] 페이지 이동:', targetPath);
                    
                    this.router.push(targetPath);
                }
            });
        }
    }

    /**
     * 로그인 상태 감지 및 알림 시스템 관리
     */
    watchLoginStatus() {
        // 초기 로그인 상태 확인
        // localStorage 변경 감지 (다른 탭에서 로그인/로그아웃)
        window.addEventListener('storage', this.handleStorageChange);
    }

    /**
     * localStorage 변경 이벤트 처리
     */
    handleStorageChange(event) {
        if (event.key === 'accessTokenHos') {
            if (event.newValue) {
                // 로그인됨
                console.log('[BrowserNotificationManager] 로그인 감지됨, 알림 시스템 초기화 시작');
                setTimeout(() => {
                    this.checkAndInitializeNotificationSystem();
                }, 1000); // 1초 후 초기화 (토큰 안정화 대기)
            } else {
                // 로그아웃됨
                console.log('[BrowserNotificationManager] 로그아웃 감지됨, 알림 시스템 정리');
                this.cleanup();
            }
        }
    }

    /**
     * 라우터 변경 이벤트 처리
     */
    handleRouteChange(to, from) {
        // 로그인 페이지에서 다른 페이지로 이동 시 (로그인 완료)
        // if (from.path.includes('/signin') && !to.path.includes('/signin')) {
      const accessTokenHos = getAccessToken()
      if (!accessTokenHos) return;
      console.log('[BrowserNotificationManager] Route Change, 알림 시스템 초기화 시작');
      setTimeout(() => {
          this.checkAndInitializeNotificationSystem();
      }, 2000); // 2초 후 초기화 (페이지 안정화 대기)
    }

    /**
     * 로그인 상태 확인 및 알림 시스템 초기화
     */
    async checkAndInitializeNotificationSystem() {
        const accessToken = getAccessToken();
        
        if (!accessToken) {
            console.log('[BrowserNotificationManager] 로그인되지 않음, 알림 시스템 초기화 건너뜀');
            return;
        }
        
        // 사용자가 수동으로 구독 해제한 경우 자동 구독 방지
        if (this.isUserUnsubscribed()) {
            const unsubscribedAt = localStorage.getItem('browserNotificationUnsubscribedAt');
            console.log('[BrowserNotificationManager] 사용자가 수동으로 구독 해제함, 자동 구독 건너뜀', unsubscribedAt);
            return;
        }
        
        if (this.isInitialized) {
            console.log('[BrowserNotificationManager] 알림 시스템이 이미 초기화됨');
            return;
        }
        
        try {
            console.log('[BrowserNotificationManager] 알림 시스템 초기화 시작');
            
            // 1. Service Worker 등록
            await this.registerServiceWorker();
            
            // 2. 알림 권한 요청
            await this.requestNotificationPermission();
            
            // 3. 푸시 구독
            await this.subscribeToPush();
            
            // 4. 정기적인 상태 확인 시작
            this.startHealthCheck();
            
            // 5. Service Worker 핑 시작 (PC 환경 최적화)
            this.startServiceWorkerPing();
            
            this.isInitialized = true;
            this.retryCount = 0;
            
            console.log('[BrowserNotificationManager] 알림 시스템 초기화 완료');
            
        } catch (error) {
            console.error('[BrowserNotificationManager] 알림 시스템 초기화 실패:', error);
            
            // 사용자가 수동으로 구독 해제한 경우 재시도하지 않음
            if (this.isUserUnsubscribed()) {
                console.log('[BrowserNotificationManager] 사용자 해제 상태로 인한 실패, 재시도 건너뜀');
                return;
            }
            
            // 재시도 로직
            this.retryCount++;
            if (this.retryCount < this.maxRetries) {
                console.log(`[BrowserNotificationManager] 알림 시스템 초기화 재시도 (${this.retryCount}/${this.maxRetries})`);
                setTimeout(() => {
                    this.checkAndInitializeNotificationSystem();
                }, 10000); // 10초 후 재시도
            }
        }
    }

    /**
     * Service Worker 등록
     */
    async registerServiceWorker() {
        if (!('serviceWorker' in navigator)) {
            throw new Error('브라우저가 Service Worker를 지원하지 않습니다');
        }
        
        try {
            // 기존 등록 확인
            let registration = await navigator.serviceWorker.getRegistration();
            
            if (registration && registration.active) {
                console.log('[BrowserNotificationManager] 기존 Service Worker 사용');
                return registration;
            }
            
            // 새로 등록
            registration = await navigator.serviceWorker.register('/sw.js', {scope: '/'});
            
            if (!registration) {
                throw new Error('Service Worker 등록 실패');
            }
            
            console.log('[BrowserNotificationManager] Service Worker 등록 성공');
            return registration;
            
        } catch (error) {
            console.error('[BrowserNotificationManager] Service Worker 등록 실패:', error);
            throw error;
        }
    }

    /**
     * 알림 권한 요청
     */
    async requestNotificationPermission() {
        if (Notification.permission === 'granted') {
            return 'granted';
        }
        
        if (Notification.permission === 'denied') {
            throw new Error('알림 권한이 거부되었습니다');
        }
        
        try {
            const permission = await Notification.requestPermission();
            if (permission !== 'granted') {
                throw new Error(`알림 권한이 거부되었습니다: ${permission}`);
            }
            
            console.log('[BrowserNotificationManager] 알림 권한 획득 성공');
            return permission;
            
        } catch (error) {
            console.error('[BrowserNotificationManager] 알림 권한 요청 실패:', error);
            throw error;
        }
    }

    /**
     * 푸시 구독
     */
    async subscribeToPush() {
        try {
            const registration = await navigator.serviceWorker.getRegistration();
            if (!registration) {
                throw new Error('Service Worker가 등록되지 않았습니다');
            }
            
            // 기존 구독 확인
            const existingSubscription = await registration.pushManager.getSubscription();
            if (existingSubscription) {
                console.log('[BrowserNotificationManager] 기존 푸시 구독 사용');
                await this.sendSubscriptionToServer(existingSubscription);
                return existingSubscription;
            }
            
            // VAPID 키 조회
            const vapidKey = await this.fetchVapidKey();
            if (!vapidKey) {
                throw new Error('VAPID 키 조회 실패');
            }
            
            // 새 구독 생성
            const applicationServerKey = this.urlBase64ToUint8Array(vapidKey);
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: applicationServerKey
            });
            await this.sendSubscriptionToServer(subscription);
            console.log('[BrowserNotificationManager] 푸시 구독 성공');

            // 브라우저 알림 구독 성공 메시지 전송
            await this.sendBrowserNotificationSubscribedMessage();

            return subscription;
            
        } catch (error) {
            console.error('[BrowserNotificationManager] 푸시 구독 실패:', error);
            throw error;
        }
    }

    // 브라우저 알림 구독 성공 메시지 전송
    async sendBrowserNotificationSubscribedMessage() {
      try {
        // 현재 창에 메시지 전송
        window.postMessage({
          type: 'BROWSER_NOTIFICATION_SUBSCRIBED',
          source: 'BrowserNotificationManager',
          timestamp: Date.now(),
          data: {
            isSubscribed: true,
          }
        }, window.location.origin);

      } catch (error) {
        console.error('[BrowserNotificationManager] 메시지 전송 실패:', error);
      }
    }

    /**
     * VAPID 키 조회
     */
    async fetchVapidKey() {
        try {
            const { data } = await this.apiInstance.get('/browser-push/vapid-public-key');
            return data.publicKey;
        } catch (error) {
            console.error('[BrowserNotificationManager] VAPID 키 조회 실패:', error);
            return null;
        }
    }

    /**
     * 서버에 구독 정보 전송
     */
    async sendSubscriptionToServer(subscription) {
        try {
            const companyId = jwtDecoder(getAccessToken());
            console.log('[BrowserNotificationManager] sendSubscriptionToServer.companyId :>> ', companyId);
            if (!companyId) {
                throw new Error('병원 ID가 없습니다');
            }
            
            const { data } = await this.apiInstance.post('/browser-push/subscribe', {
                userId: companyId,
                userType: 'company',
                endpoint: subscription.endpoint,
                p256dh: this.arrayBufferToBase64(subscription.getKey('p256dh')),
                auth: this.arrayBufferToBase64(subscription.getKey('auth'))
            });

            localStorage.setItem('browserNotificationSubscriptionInfo', JSON.stringify(data));
            console.log('[BrowserNotificationManager] 서버 구독 등록 성공');
            
        } catch (error) {
            console.error('[BrowserNotificationManager] 서버 구독 등록 실패:', error);
            throw error;
        }
    }

    /**
     * 정기적인 알림 수신 상태 확인 시작
     */
    startHealthCheck() {
        if (this.checkTimer) {
            clearInterval(this.checkTimer);
        }
        
        this.checkTimer = setInterval(async () => {
            await this.checkHealth();
        }, this.checkInterval);
        
        console.log('[BrowserNotificationManager] 알림 상태 정기 확인 시작 (5분 간격)');
    }

    /**
     * 알림 수신 상태 확인 및 복구
     */
    async checkHealth() {
        try {
            console.log('[BrowserNotificationManager] 알림 수신 상태 확인 중...');
            
            // 사용자가 수동으로 구독 해제한 경우 자동 복구하지 않음
            if (this.isUserUnsubscribed()) {
                console.log('[BrowserNotificationManager] 사용자가 수동으로 구독 해제함, 자동 복구 건너뜀');
                return;
            }
            
            // 1. Service Worker 상태 확인
            const registration = await navigator.serviceWorker.getRegistration();
            if (!registration || !registration.active) {
                console.log('[BrowserNotificationManager] Service Worker 비활성화 감지, 재등록 시도');
                await this.registerServiceWorker();
            }
            
            // 2. 푸시 구독 상태 확인
            const subscription = await registration.pushManager.getSubscription();
            if (!subscription) {
                console.log('[BrowserNotificationManager] 푸시 구독 만료 감지, 재구독 시도');
                await this.subscribeToPush();
            }
            
            // 3. 알림 권한 상태 확인
            if (Notification.permission !== 'granted') {
                console.log('[BrowserNotificationManager] 알림 권한 변경 감지');
                if (Notification.permission === 'denied') {
                    console.warn('[BrowserNotificationManager] 알림 권한이 거부되었습니다');
                }
            }
            
            console.log('[BrowserNotificationManager] 알림 수신 상태 정상');
            
        } catch (error) {
            console.error('[BrowserNotificationManager] 알림 상태 확인 중 오류:', error);
            
            // 사용자가 수동으로 구독 해제한 경우 재초기화하지 않음
            if (this.isUserUnsubscribed()) {
                console.log('[BrowserNotificationManager] 사용자 해제 상태로 인한 오류, 재초기화 건너뜀');
                return;
            }
            
            // 오류 발생 시 시스템 재초기화 시도
            this.isInitialized = false;
            setTimeout(() => {
                this.checkAndInitializeNotificationSystem();
            }, 30000); // 30초 후 재시도
        }
    }

    /**
     * 알림 시스템 정리
     */
    cleanup() {
        if (this.checkTimer) {
            clearInterval(this.checkTimer);
            this.checkTimer = null;
        }
        
        if (this.pingTimer) {
            clearInterval(this.pingTimer);
            this.pingTimer = null;
        }
        
        this.isInitialized = false;
        this.retryCount = 0;
        
        console.log('[BrowserNotificationManager] 알림 시스템 정리 완료');
    }

    /**
     * 리소스 정리 (컴포넌트 언마운트 시 호출)
     */
    destroy() {
        this.cleanup();
        
        // 이벤트 리스너 제거
        window.removeEventListener('storage', this.handleStorageChange);
        
        console.log('[BrowserNotificationManager] 리소스 정리 완료');
    }

    /**
     * 유틸리티 함수: Base64 문자열을 Uint8Array로 변환
     */
    urlBase64ToUint8Array(base64String) {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding)
            .replace(/-/g, '+')
            .replace(/_/g, '/');

        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);

        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    }

    /**
     * 유틸리티 함수: ArrayBuffer를 Base64 문자열로 변환
     */
    arrayBufferToBase64(buffer) {
        const bytes = new Uint8Array(buffer);
        let binary = '';
        for (let i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    }

    /**
     * Service Worker 핑 시작 (PC 환경 최적화)
     */
    startServiceWorkerPing() {
        if (this.pingTimer) {
            clearInterval(this.pingTimer);
        }
        
        this.pingTimer = setInterval(async () => {
            await this.pingServiceWorker();
        }, this.pingInterval);
        
        console.log('[BrowserNotificationManager] Service Worker 핑 시작 (30초 간격)');
    }

    /**
     * Service Worker 핑 실행
     */
    async pingServiceWorker() {
        if (this.isUserUnsubscribed()) {
          this.cleanup();
          return
        };
        try {
            if (!('serviceWorker' in navigator)) {
                return;
            }

            const registration = await navigator.serviceWorker.getRegistration();
            
            if (!registration) {
                console.log('[BrowserNotificationManager] Service Worker 등록 없음, 재등록 시도');
                await this.registerServiceWorker();
                return;
            }

            if (!registration.active) {
                console.log('[BrowserNotificationManager] Service Worker 비활성 상태, 재등록 시도');
                await this.registerServiceWorker();
                return;
            }

            // Service Worker에 핑 메시지 전송
            registration.active.postMessage({
                type: 'PING',
            });

            // 푸시 구독 상태도 함께 확인
            const subscription = await registration.pushManager.getSubscription();
            if (!subscription) {
                console.log('[BrowserNotificationManager] 푸시 구독 만료 감지, 재구독 시도');
                await this.subscribeToPush();
            }
        } catch (error) {
            console.error('[BrowserNotificationManager] Service Worker ping 실패:', error);
            
            // 사용자가 수동으로 구독 해제한 경우 핑 중단하지 않음 (시스템 문제일 수 있음)
            if (!this.isUserUnsubscribed()) {
                // 오류 발생 시 시스템 재초기화 시도
                setTimeout(() => {
                    this.checkAndInitializeNotificationSystem();
                }, 5000); // 5초 후 재시도
            }
        }
    }
}

export default BrowserNotificationManager; 